/**
 * widgets/SearchSelect.ts · v356 通用可搜 select (复用 v200 v161 TN_SEARCH_SELECT)
 *
 * 场景: 200+ 客户/产品/SO/PO 的 select 滚轮翻不到 → 加搜索框
 *
 * 用法:
 *   1. 自动 attach (推荐): page render 后调 `initSearchSelectAll(container)`
 *      → 扫所有 option > 20 的 select 自动升级
 *   2. 显式 attach: `attachSearchSelect(selectEl)`
 *
 * 设计:
 *   - 包装 select 进 div · 加 search input 浮在 dropdown 上
 *   - input 时 filter options (className 'tn-ss-hidden')
 *   - Enter 选首匹配 · Esc 取消 · ↑↓ 键盘导航
 *   - cap 100 options 防 perf
 *   - 跳过已 attach 的 (防重复)
 */

const ATTACHED_FLAG = 'tnSsAttached';
const MIN_OPTIONS = 20;     // 仅 ≥ 20 option 启用搜索
const CAP_OPTIONS = 200;    // 超过 200 option 截 200 防卡

export function attachSearchSelect(select: HTMLSelectElement): void {
  if (select.dataset[ATTACHED_FLAG] === '1') return;
  if (select.options.length < MIN_OPTIONS) return;

  // cap option (防卡)
  if (select.options.length > CAP_OPTIONS) {
    for (let i = select.options.length - 1; i >= CAP_OPTIONS; i--) {
      select.options.remove(i);
    }
  }

  select.dataset[ATTACHED_FLAG] = '1';

  // 包装 wrapper
  const wrapper = document.createElement('div');
  wrapper.className = 'tn-ss-wrapper';
  wrapper.style.cssText = 'position:relative;display:block';
  select.parentNode!.insertBefore(wrapper, select);
  wrapper.appendChild(select);

  // 注入搜索 input (默认隐藏 · select focus 时显)
  const searchBox = document.createElement('input');
  searchBox.type = 'text';
  searchBox.className = 'tn-ss-search';
  searchBox.placeholder = `🔍 搜索 ${select.options.length} 项...`;
  searchBox.style.cssText = `
    position:absolute; top:100%; left:0; right:0; z-index:99;
    padding:8px 12px; border:2px solid #3b82f6; border-radius:6px;
    background:white; font-size:14px;
    box-shadow:0 4px 12px rgba(0,0,0,0.12);
    display:none;
  `;
  wrapper.appendChild(searchBox);

  // dropdown clone (因为 native select dropdown 无法被 input 覆盖)
  const dropdown = document.createElement('div');
  dropdown.className = 'tn-ss-dropdown';
  dropdown.style.cssText = `
    position:absolute; top:calc(100% + 42px); left:0; right:0; z-index:98;
    background:white; border:1px solid #d1d5db; border-radius:6px;
    max-height:280px; overflow-y:auto;
    box-shadow:0 6px 20px rgba(0,0,0,0.15);
    display:none;
  `;
  wrapper.appendChild(dropdown);

  // 构 dropdown options
  const renderDropdown = (filter = ''): void => {
    dropdown.innerHTML = '';
    const lcFilter = filter.toLowerCase().trim();
    let visible = 0;
    for (let i = 0; i < select.options.length; i++) {
      const opt = select.options[i];
      const txt = opt.textContent || '';
      if (lcFilter && !txt.toLowerCase().includes(lcFilter)) continue;
      visible++;
      const item = document.createElement('div');
      item.className = 'tn-ss-item';
      item.style.cssText = `padding:8px 12px; cursor:pointer; font-size:13px; border-bottom:1px solid #f3f4f6; transition:background 0.1s;`;
      item.textContent = txt;
      item.onmouseenter = () => { item.style.background = '#dbeafe'; };
      item.onmouseleave = () => { item.style.background = ''; };
      item.onclick = () => {
        select.value = opt.value;
        select.selectedIndex = i;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        searchBox.style.display = 'none';
        dropdown.style.display = 'none';
        searchBox.value = '';
      };
      dropdown.appendChild(item);
    }
    if (visible === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'padding:12px;text-align:center;color:#9ca3af;font-size:12px';
      empty.textContent = '📭 无匹配 / Không khớp';
      dropdown.appendChild(empty);
    }
  };

  // Show 搜索框 + dropdown 当 select focus
  let opening = false;
  select.addEventListener('mousedown', (e) => {
    if (opening) return;
    opening = true;
    e.preventDefault();
    // 阻止 native dropdown 显
    select.blur();
    searchBox.style.display = 'block';
    dropdown.style.display = 'block';
    renderDropdown();
    setTimeout(() => searchBox.focus(), 30);
    opening = false;
  });

  // 搜索 input 事件
  searchBox.addEventListener('input', () => {
    renderDropdown(searchBox.value);
  });

  searchBox.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      searchBox.style.display = 'none';
      dropdown.style.display = 'none';
      searchBox.value = '';
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // 选首匹配
      const first = dropdown.querySelector<HTMLDivElement>('.tn-ss-item');
      if (first) first.click();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const first = dropdown.querySelector<HTMLDivElement>('.tn-ss-item');
      if (first) (first as HTMLElement).focus();
    }
  });

  // click outside 关
  document.addEventListener('click', (e) => {
    if (!wrapper.contains(e.target as Node)) {
      searchBox.style.display = 'none';
      dropdown.style.display = 'none';
    }
  });
}

/**
 * 自动 attach 容器内所有 option > 20 的 select
 * page render 后调 setTimeout(initSearchSelectAll, 50)
 */
export function initSearchSelectAll(container: HTMLElement | Document = document): void {
  const selects = container.querySelectorAll<HTMLSelectElement>('select');
  selects.forEach(s => {
    // 跳过 [data-tn-no-search] 显式禁用
    if (s.dataset.tnNoSearch === '1') return;
    attachSearchSelect(s);
  });
}

/**
 * 路由完成后自动扫一次 (event-driven · 不污染轮询)
 */
export function initSearchSelectAuto(): void {
  document.addEventListener('tn-render-done', () => {
    setTimeout(() => initSearchSelectAll(document), 80);
  });
}
